// Content derived from Technical Specification (ТЗ)

// Token lifetimes shown to users.  Single source for every page: the landing,
// Profile and Docs used to spell them out separately and drifted apart (the
// feature list said 60 хв while the realm issues 5-minute tokens).  Keep in
// sync with `accessTokenLifespan` / `ssoSessionIdleTimeout` in
// config/keycloak/govpoll-api-realm.json.
const ACCESS_TOKEN_TTL = "5 хвилин";
const REFRESH_TOKEN_TTL = "30 хвилин";

export const PROJECT_INFO = {
  title: "Система збору та публікації структурованих даних про голосування органів влади",
  shortTitle: "Voting Data API",

  description: "Система збирає з різних джерел (сайти рад, публічні/закриті API, PDF/CSV/XLSX, RSS) дані про голосування депутатів, порядок денний, депутатів і фракції/партії, присутність, допоміжні довідники та надає авторизований REST API для їх споживання сторонніми системами.",

  problem: "Сьогодні дані про голосування депутатів у місцевих радах та інших органах влади розпорошені по різних сайтах, форматах і платформах. Кожен орган публікує інформацію у власному форматі — HTML-таблиці, PDF-файли, структуровані дані через API. Це створює значні труднощі для журналістів, дослідників, громадських активістів та аналітиків.",

  solution: "Система створює єдиний структурований репозиторій з прозорим походженням (provenance) кожного запису та зручним програмним доступом через REST API.",

  businessValue: "Система дозволить журналістам швидко аналізувати голосування, дослідникам будувати статистичні моделі політичної поведінки, а громадським активістам — створювати інструменти громадського контролю без необхідності вручну збирати дані з десятків різних джерел."
};

export const FEATURES = [
  {
    title: "Множинні джерела даних",
    description: "Підтримка HTML сторінок, PDF/DOCX/XLSX/CSV файлів, публічних/закритих API та RSS стрічок."
  },
  {
    title: "ETL-пайплайн",
    description: "Асинхронна система обробки з чергами завдань, підтримкою ретраїв та exponential backoff."
  },
  {
    title: "Єдина модель даних",
    description: "Повна темпоральність даних, стабільна ідентифікація через UUID, повне provenance кожного запису."
  },
  {
    title: "REST API з JWT",
    description: `Авторизований доступ через JWT токени (тривалість ${ACCESS_TOKEN_TTL}) з опційним refresh-токеном.`
  },
  {
    title: "Багата фільтрація",
    description: "Фільтрація за органом, сесією, засіданням, датою, фракцією, депутатом та результатом голосування."
  },
  {
    title: "Прозорість походження",
    description: "Кожен запис містить метадані про джерело: URL, час завантаження, версію парсера, хеш контенту."
  }
];

// `required` – параметри, без яких запит поверне 422. Тримати синхронно з
// app/api/lakehouse/*.py: там вони оголошені як Query(...) без значення за
// замовчуванням.
export const API_ENDPOINTS = [
  { method: "GET", path: "api/v1/lakehouse/organs", description: "Отримати перелік органів влади", required: [] },
  { method: "GET", path: "api/v1/lakehouse/seasons", description: "Отримати перелік сесій/скликань", required: ["organ_id"] },
  { method: "GET", path: "api/v1/lakehouse/meetings", description: "Отримати інформацію про засідання", required: ["season_id", "date_from", "date_to"] },
  { method: "GET", path: "api/v1/lakehouse/votings", description: "Отримати дані про голосування", required: ["law_id", "date_from", "date_to"] },
  { method: "GET", path: "api/v1/lakehouse/vote-records", description: "Отримати рядки голосів депутатів", required: ["voting_id"] },
  { method: "GET", path: "api/v1/lakehouse/deputies", description: "Отримати інформацію про депутатів", required: [] },
  { method: "GET", path: "api/v1/lakehouse/fractions", description: "Отримати дані про фракції", required: ["organ_id"] }
];

// Ролі realm `govpoll-api` — у порядку спадання старшинства. Імена мають
// збігатися з config/keycloak/govpoll-api-realm.json: саме за ними бек звіряє
// доступ. Акаунт може мати кілька ролей одночасно — діє найстарша за цим
// списком (той самий принцип, що й ROLE_SENIORITY у AdminUsers.jsx).
export const USER_ROLES = [
  {
    name: "super-admin",
    label: "Super Admin",
    description: "Найвищий ранг — керує обліковими записами, ролями та ключами доступу, бачить усіх користувачів."
  },
  {
    name: "admin",
    label: "Адміністратор",
    description: "Керує обліковими записами та ролями, окрім super-admin. Повний доступ до Public API."
  },
  {
    name: "engineer",
    label: "Інженер",
    description: "Доступ до Public API на читання та запис. Без керування користувачами."
  },
  {
    name: "user",
    label: "Користувач",
    description: "Аналітик/Інтегратор — доступ до Public API лише на читання даних."
  }
];

export const API_LIMITS = {
  rateLimit: "60 запитів/хвилину на токен",
  tokenLifetime: ACCESS_TOKEN_TTL,
  refreshLifetime: REFRESH_TOKEN_TTL,
  paginationLimit: "≤ 200 записів на запит"
};